import {
  Component,
  ContentChildren,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
  FormArray,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogClose,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { BehaviorSubject, distinctUntilChanged, finalize, forkJoin, map, Observable, Subscription, take } from 'rxjs';
import {
  ButtonModel,
  LookupModel,
  PbxAccountButtonTemplateModel,
  PbxAccountDeviceButtonsModel,
  PbxAccountModel,
} from '../../../../../../api/models';
import { NotificationService } from '../../../../../../notification/notification.service';
import { LocalPbxService } from '../../../../../local-pbx.service';
import { MatConfirmDialogComponent } from '../../../../../../dialogs/mat-confirm/mat-confirm-dialog.component';
import { DateTime } from 'luxon';
import {
  DeviceButtonTemplatesListDialogComponent,
  DeviceButtonTemplatesListDialogData,
} from '../../device-button-templates/device-button-templates-list-dialog/device-button-templates-list-dialog.component';
import { FlexModule, ExtendedModule } from '@angular/flex-layout';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { DeviceButtonComponent } from '../../device-button/device-button.component';
import { DeviceButtonSharedService } from '../../services/device-button-shared.service';
import {
  DeviceButtonTemplateEditDialogComponent,
  DeviceButtonTemplateEditDialogData,
} from '../../device-button-templates/device-button-template-edit-dialog/device-button-template-edit-dialog.component';
import { MatMenuModule } from '@angular/material/menu';
import { YealinkExp43Component } from '../yealink-devices/expansions/yealink-exp43/yealink-exp43.component';
import { PolycomVvxColorComponent } from '../polycom-devices/expansions/polycom-vvx-color/polycom-vvx-color.component';
import { MatSimpleSelectComponent } from '../../../../../../editors/mat-simple-select/mat-simple-select.component';
import { PolycomVvxEmComponent } from '../polycom-devices/expansions/polycom-vvx-em/polycom-vvx-em.component';
import { PolycomVvxEm50Component } from '../polycom-devices/expansions/polycom-vvx-em50/polycom-vvx-em50.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ExpansionModelCode } from '../../interfaces';
import { ButtonTemplateType, ButtonTypes, DeviceVendor } from '../../enums';
import { ALL_EXPANSION_MODELS } from '../../constants/device-shared';
import { EXPANSION_METADATA } from '../../constants/expansion-metadata';
import { PHONE_METADATA } from '../../constants/phone-metadata';
import { POLYCOM_EXPANSION_OPTIONS, YEALINK_EXPANSION_OPTIONS } from '../../constants/expansion-options';

export interface DeviceButtonsSettingsDialogData {
  deviceId: string;
  extension: PbxAccountModel;
  isDefault?: boolean;
}

@Component({
  selector: 'app-device-base',
  templateUrl: './device-base.component.html',
  styleUrls: ['./device-base.component.scss'],
  standalone: true,
  imports: [
    FlexModule,
    MatProgressSpinner,
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    MatDialogContent,
    MatAccordion,
    FormsModule,
    ReactiveFormsModule,
    MatDialogActions,
    MatButton,
    Angular2PromiseButtonModule,
    AsyncPipe,
    DeviceButtonComponent,
    MatMenuModule,
    YealinkExp43Component,
    PolycomVvxColorComponent,
    PolycomVvxEmComponent,
    PolycomVvxEm50Component,
    MatSimpleSelectComponent,
    MatTooltipModule,
    ExtendedModule,
  ],
})
export class DeviceBaseComponent implements OnInit, OnDestroy {
  @ContentChildren('lineKeyButton', { descendants: true }) lineKeyButtons!: QueryList<ElementRef>;
  @ViewChildren(DeviceButtonComponent) deviceButtonComponents!: QueryList<DeviceButtonComponent>;
  @ViewChild('expansionModule') expansionModule!: any;

  public selectableDevices: LookupModel[] = [];

  public activePage = 0;
  public activeButton = 0;
  public isExpansionMode = false;
  public selectedDeviceId: number = -1;
  public selectedDeviceModel: ExpansionModelCode = 'phone';

  public readonly speedDialNumberRegex = new RegExp('^[0-9]{10}$');
  public readonly buttonTypes = ButtonTypes;

  public readonly formSubmitted$ = new BehaviorSubject<boolean>(false);

  private _device: PbxAccountDeviceButtonsModel;

  private readonly _subscriptions = new Subscription();
  private readonly _form = this._fb.group({
    deviceButtons: this._fb.array([]),
    expansions: this._fb.array([]),
  });

  private readonly _dataLoadedSub$ = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly _data: DeviceButtonsSettingsDialogData,
    private readonly _dialog: MatDialog,
    private readonly _pbxService: LocalPbxService,
    private readonly _dialogRef: MatDialogRef<DeviceBaseComponent>,
    private readonly _fb: UntypedFormBuilder,
    private readonly _notificationService: NotificationService,
    private readonly _deviceButtonSharedService: DeviceButtonSharedService,
  ) {}

  get dateInformation(): { date: string; dateMMDD: string; time: string; timePMAM: string; hours: string; minutes: string; fullDate: string } {
    const localTime = DateTime.local();

    return {
      date: localTime.toFormat('ccc, LLL d'),
      dateMMDD: localTime.toFormat('MM/dd'),
      time: localTime.toFormat('HH:mm'),
      timePMAM: localTime.toFormat('hh:mm a'),
      hours: localTime.toFormat('HH'),
      minutes: localTime.toFormat('mm'),
      fullDate: localTime.toFormat('ccc, LLL d'),
    };
  }

  get extension(): PbxAccountModel {
    return this._data.extension;
  }

  get expansionOptions(): LookupModel[] {
    switch (this._device?.vendor) {
      case DeviceVendor.Yealink:
        return YEALINK_EXPANSION_OPTIONS;

      case DeviceVendor.Polycom:
      case DeviceVendor.Poly:
        return POLYCOM_EXPANSION_OPTIONS;
      default:
        return [];
    }
  }

  get isPagingAvailable(): boolean {
    if (!this.isExpansionMode && this.isDefaultDevice) {
      return false;
    }

    if (this.isExpansionMode) {
      return !!EXPANSION_METADATA[this.selectedDeviceModel]?.isPagingAvailable;
    }

    const { vendor, model } = this._device || {};
    const modelKey = vendor === DeviceVendor.Yealink ? model?.slice(0, 3) : model;

    return !!PHONE_METADATA[modelKey]?.isPagingAvailable;
  }

  get buttonsPerPage(): number {
    if (this.isExpansionMode) {
      const { deviceButtonsCount } = EXPANSION_METADATA[this.selectedDeviceModel] || {};
      return deviceButtonsCount ?? 0;
    }

    const { vendor, model } = this._device || {};
    const modelKey = vendor === DeviceVendor.Yealink ? model?.slice(0, 3) : model;
    return PHONE_METADATA[modelKey]?.deviceButtonsCount ?? 0;
  }

  get pageCount(): number {
    if (this.isExpansionMode) {
      const { buttonsCount, deviceButtonsCount } = EXPANSION_METADATA[this.selectedDeviceModel] || {};
      if (!buttonsCount || !deviceButtonsCount) return 1;
      return Math.ceil(buttonsCount / deviceButtonsCount);
    }

    const { vendor, model } = this._device || {};
    const modelKey = vendor === DeviceVendor.Yealink ? model?.slice(0, 3) : model;
    const { buttonsCount, deviceButtonsCount } = PHONE_METADATA[modelKey] || {};
    return Math.ceil(buttonsCount / deviceButtonsCount);
  }

  get hasDedicatedPagingButton(): boolean {
    if (this.isExpansionMode) return false;
    const { vendor, model } = this._device || {};
    const modelKey = vendor === DeviceVendor.Yealink ? model?.slice(0, 3) : model;
    return !!PHONE_METADATA[modelKey]?.hasDedicatedPagingButton;
  }

  get isDefaultDevice(): boolean {
    return !!this._data.isDefault;
  }

  get deviceModelTitle(): string {
    return `${this._device?.vendor} ${this._device?.model}`;
  }

  get activeButtonsFormArray(): UntypedFormArray {
    return this.isExpansionMode ? this.expansionButtonsFormArray(this.selectedDeviceId) : this.deviceButtonsFormArray;
  }

  get expansionFormArray(): UntypedFormArray {
    return this._form.get('expansions') as FormArray;
  }

  get deviceButtonsFormArray(): UntypedFormArray {
    return this._form.get('deviceButtons') as UntypedFormArray;
  }

  get isExpansionAvailable(): boolean {
    return !!this.expansionButtonsFormArray.length;
  }

  get isPagingActive(): boolean {
    if (!this.isPagingAvailable) {
      return false;
    }

    const btns = this.deviceButtonsFormArray.getRawValue()?.slice(this.buttonsPerPage);
    return btns?.some(btn => btn.type !== 'none');
  }

  get paginatedButtons(): UntypedFormGroup[] {
    // Do not apply pagination if paging not available for device
    if (!this.isPagingAvailable && !this.isExpansionMode) {
      return this.deviceButtonsFormArray.controls as UntypedFormGroup[];
    }

    // Expansion module currently available only for yealink devices
    if (this.isExpansionMode && !this.isPagingAvailable) {
      return this.expansionButtonsFormArray(this.selectedDeviceId).controls as UntypedFormGroup[];
    }

    const buttons = this.isExpansionMode
      ? this.expansionButtonsFormArray(this.selectedDeviceId).controls
      : this.deviceButtonsFormArray.controls;

    const buttonsInPage = this.buttonsPerPage;
    let startIndex = this.activePage * buttonsInPage;
    let endIndex = (this.activePage + 1) * buttonsInPage;

    if (!this.isExpansionMode && this.hasDedicatedPagingButton && this.isPagingActive) {
      startIndex = this.activePage * (buttonsInPage - 1);
      endIndex = startIndex + (buttonsInPage - 1);
    }

    return (buttons?.slice(startIndex, endIndex) || []) as UntypedFormGroup[];
  }

  get isFormValid(): boolean {
    return this._form?.valid;
  }

  get isDataLoaded$(): Observable<boolean> {
    return this._dataLoadedSub$.asObservable();
  }

  get canAddExpansion(): boolean {
    return this.selectableDevices.length < 4;
  }

  public ngOnInit(): void {
    this._subscriptions.add(this.subscribeDeviceData());
    this._subscriptions.add(this.subscribeFormChanges());
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public expansionButtonsFormArray(index: number): UntypedFormArray {
    return this.expansionFormArray.at(index)?.get('buttons') as FormArray;
  }

  public extensionNameForDevice(truncate?: number): string {
    const { firstName = '', displayName = '' } = this._data.extension || {};
    const fullName = `${firstName} ${displayName}`;

    return truncate && fullName.length > truncate ? `${fullName.substring(0, truncate)}...` : fullName;
  }

  public getLabel(btnIndex: number, labelLength: number = 7): string {
    const button = this.paginatedButtons?.at(btnIndex)?.getRawValue();

    if (!button) {
      return '';
    }

    const { label, parameter, type } = button;
    const text = label || this._deviceButtonSharedService.getDefaultLabel(type, parameter);

    return text.length > labelLength ? text.slice(0, labelLength) : text;
  }

  public onActiveButtonToggle(event: Event, btnIndex: number): void {
    const clickedPath = event?.target as Element;
    const expansion = this.deviceButtonComponents.toArray()?.[btnIndex]?.expansionPanel;
    const isActive = clickedPath?.classList?.contains('active');

    if (isActive) {
      expansion()?.close();
      this.activeButton = null;
    } else {
      expansion()?.open();
    }
  }

  public onExpansionToggle(btnIndex: number, isOpen: boolean): void {
    const buttons = this.isExpansionMode ? this.expansionModule?.getLineKeyButtons() : this.lineKeyButtons?.toArray();
    const button = buttons?.[btnIndex]?.nativeElement as Element;

    if (isOpen) {
      this.activeButton = btnIndex;
    }

    button?.classList?.toggle('active', isOpen);
  }

  public onButtonsPageChange(index?: number): void {
    const pages = this.pageCount;
    this.activePage = (index ?? this.activePage + 1) % pages;

    this.onExpansionToggle(this.activeButton, false);
  }

  public onSave(): void {
    if (!this.isFormValid) {
      return;
    }

    this.formSubmitted$.next(true);

    const { deviceButtons: buttons, expansions } = this._form.getRawValue() || {};

    this._pbxService
      .savePbxAccountDeviceButtons(this._data.deviceId, { buttons, expansions })
      .pipe(
        take(1),
        finalize(() => {
          this.formSubmitted$.next(false);
        }),
      )
      .subscribe({ next: () => this._notificationService.notifySuccess('Successfully saved device buttons') });
  }

  public onButtonsClear(): void {
    const dialogRef = this._dialog.open(MatConfirmDialogComponent, {
      data: {
        title: 'Clear Buttons',
        text: `Are you sure you’d like to clear all buttons? This will erase all of your ${this.isExpansionMode ? 'expansion module' : 'phone'} button settings.`,
        actionTitle: 'Yes',
        showCancelBtn: true,
        hideCancel: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.clearButtons();
        }
      });
  }

  public onManageTemplates(): void {
    const dialogRef = this._dialog.open<
      DeviceButtonTemplatesListDialogComponent,
      DeviceButtonTemplatesListDialogData,
      { template: PbxAccountButtonTemplateModel; expansionId?: number }
    >(DeviceButtonTemplatesListDialogComponent, {
      data: {
        availableExpansionDevices: this.selectableDevices.filter(device => device.id !== -1),
      },
      width: '600px',
      height: '80%',
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(data => {
        const { template, expansionId } = data || {};

        if (template) {
          this.applyButtonTemplate(template, expansionId);
          this._notificationService.notifySuccess('Successfully applied button template');
        }
      });
  }

  public onSaveAsTemplate(): void {
    this._dialog.open<DeviceButtonTemplateEditDialogComponent, DeviceButtonTemplateEditDialogData, boolean>(
      DeviceButtonTemplateEditDialogComponent,
      {
        data: {
          templateId: 'new',
          deviceButtons: this.activeButtonsFormArray.getRawValue(),
          templateType: this.isExpansionMode ? ButtonTemplateType.Expansion : ButtonTemplateType.Phone,
        },
        width: '800px',
        height: '70%',
      },
    );
  }

  public onRemoveExpansion(): void {
    this.expansionFormArray.removeAt(this.selectedDeviceId);

    if (!this.selectedDeviceId) {
      this.selectedDeviceId = -1;
    } else {
      this.selectedDeviceId = this.selectedDeviceId - 1;
    }

    this.prepareSelectableDevices();
    this.onDeviceSwitch(this.selectedDeviceId);
  }

  public onDeviceSwitch(selectedDeviceId: number | string): void {
    this.selectedDeviceId = Number(selectedDeviceId);

    this.selectedDeviceModel = this.selectableDevices.find(device => device.id === this.selectedDeviceId)
      ?.metadata as ExpansionModelCode;

    this.isExpansionMode = this.selectedDeviceModel !== 'phone';

    this.activePage = 0;
    this.onButtonsPageChange(0);

    if (this.isExpansionMode) {
      const { buttonsCount } = EXPANSION_METADATA[this.selectedDeviceModel] || {};

      this.syncExpansionButtonsFormArray(buttonsCount);
    }
  }

  public onAddExpansion(item: LookupModel): void {
    if (!this.canAddExpansion) {
      return;
    }

    const emptyIndex = this.expansionFormArray.controls.findIndex(group => !group.get('model')?.value);

    if (emptyIndex === -1) {
      this.expansionFormArray.push(
        this._deviceButtonSharedService.createExpansionGroup({ model: item.id as string, buttons: [] }),
      );

      this.selectedDeviceId = this.expansionFormArray.length - 1;
    } else {
      // Use the existing empty model index and update its model value
      const group = this.expansionFormArray.at(emptyIndex);
      group.get('model')?.setValue(item.id);
      this.selectedDeviceId = emptyIndex;
    }

    this.prepareSelectableDevices();
    this.onDeviceSwitch(this.selectedDeviceId);
  }

  private syncExpansionButtonsFormArray(expansionButtonsCount: number): void {
    const currentExpansionForm = this.expansionButtonsFormArray(this.selectedDeviceId);

    const currentCount = currentExpansionForm.length;
    const buttonsCount = expansionButtonsCount || 0;

    if (currentCount < buttonsCount) {
      for (let i = currentCount; i < buttonsCount; i++) {
        const btnModel: ButtonModel = {
          name: 'Expansion Key',
          mode: '1',
          sort: i + 1,
        };

        currentExpansionForm.push(this._deviceButtonSharedService.createButtonGroup(btnModel));
      }

      return;
    }

    for (let i = currentCount - 1; i >= buttonsCount; i--) {
      currentExpansionForm.removeAt(i);
    }
  }

  private applyButtonTemplate(template: PbxAccountButtonTemplateModel, expansionId?: number): void {
    const { buttons, type } = template;

    const buttonsToUpdate =
      type === ButtonTemplateType.Expansion ? this.expansionButtonsFormArray(expansionId) : this.deviceButtonsFormArray;

    for (let i = 0; i < buttonsToUpdate.length; i++) {
      const button = buttons[i];
      if (!button) {
        break;
      }

      buttonsToUpdate.at(i).patchValue(button);
    }
  }

  private populateButtonsForm(): void {
    const { buttons, expansions } = this._device || {};

    buttons.forEach(button => {
      const buttonGroup = this._deviceButtonSharedService.createButtonGroup(button);
      this.deviceButtonsFormArray.push(buttonGroup);
    });

    expansions.forEach(exp => {
      this.expansionFormArray.push(this._deviceButtonSharedService.createExpansionGroup(exp));
    });
  }

  private clearButtons(): void {
    this.activeButtonsFormArray.controls.forEach(group => {
      const type = group?.get('type');

      type.setValue(ButtonTypes.None);
      this._deviceButtonSharedService.onButtonTypeChange(ButtonTypes.None, group as UntypedFormGroup);
    });

    this._form.updateValueAndValidity();
  }

  private prepareSelectableDevices(): void {
    const currentDeviceExpansions =
      this.expansionFormArray
        .getRawValue()
        ?.map(exp => exp.model)
        ?.filter(Boolean) || [];

    this.selectableDevices = [
      { id: -1, text: this.deviceModelTitle, metadata: 'phone' },
      ...(currentDeviceExpansions || []).map((data, index) => ({
        id: index,
        text: `${ALL_EXPANSION_MODELS[data] || `Expansion ${index + 1}`}`,
        metadata: data,
      })),
    ];
  }

  private subscribeDeviceData(): Subscription {
    const { deviceId } = this?._data;

    return forkJoin([
      this._pbxService.getPbxAccountDeviceButtons(deviceId).pipe(take(1)),
      this._pbxService.getAccountsLookupForDevice().pipe(take(1)),
    ])
      .pipe(finalize(() => this._dataLoadedSub$.next(true)))
      .subscribe({
        next: ([device, parameterAccounts]) => {
          if (!device) {
            this._dialogRef.close();
            this._notificationService.notifyDanger('Device not found');
            return;
          }

          this._device = device;

          if (!device.buttons?.length) {
            this._dialogRef.close();
            this._notificationService.notifyDanger(`Buttons for ${this.deviceModelTitle} not available`);
            return;
          }

          this._deviceButtonSharedService.prepareData(parameterAccounts?.data || [], this.extension);
          this.populateButtonsForm();
          this.prepareSelectableDevices();
        },
        error: () => {
          this._dialogRef.close();
        },
      });
  }

  private subscribeFormChanges(): Subscription {
    return this.deviceButtonsFormArray.valueChanges
      .pipe(
        map(() => this.isPagingActive),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        const openPanel = this.deviceButtonComponents.toArray().find(btn => btn.expansionPanel()?.expanded);

        openPanel?.expansionPanel().close();
      });
  }
}
